import {gql, useMutation, useQuery} from '@apollo/client'
import {Button, Typography} from 'nf-ui'
import React, {useState} from 'react'
import {useAlert} from 'react-alert'
import {ModalLayout} from '~/components/ModalLayout'
import {CurrentOrganisation} from '~/components/CurrentOrganisationContext'
import {StyledTextArea, VisibleInput} from '~/components/Primitives/InputFields/VisibleInput'
import {getGraphQLErrorMessage} from '~/util'

const CHILD_DIRECTORIES = gql`
    query ChildDirectoriesForParent($parentOrganisationIdStr: String!) {
        childDirectoriesForParent(parentOrganisationIdStr: $parentOrganisationIdStr) {
            idStr
            name
            filterRules
            dataSourceIdStr
            userSyncEnabled
        }
    }
`

const CREATE_CHILD = gql`
    mutation CreateChildDirectory($parentOrganisationIdStr: String!, $input: CreateChildDirectoryInput!) {
        createChildDirectory(parentOrganisationIdStr: $parentOrganisationIdStr, input: $input) {
            idStr
            name
            filterRules
            dataSourceIdStr
            userSyncEnabled
        }
    }
`

const DELETE_CHILD = gql`
    mutation DeleteChildDirectory($parentOrganisationIdStr: String!, $childOrganisationIdStr: String!) {
        deleteChildDirectory(
            parentOrganisationIdStr: $parentOrganisationIdStr
            childOrganisationIdStr: $childOrganisationIdStr
        )
    }
`

const DETACH_CHILD = gql`
    mutation DetachChildDirectory($parentOrganisationIdStr: String!, $childOrganisationIdStr: String!) {
        detachChildDirectory(
            parentOrganisationIdStr: $parentOrganisationIdStr
            childOrganisationIdStr: $childOrganisationIdStr
        )
    }
`

const SET_USER_SYNC_ENABLED = gql`
    mutation SetChildDirectoryUserSyncEnabled(
        $parentOrganisationIdStr: String!
        $childOrganisationIdStr: String!
        $enabled: Boolean!
    ) {
        setChildDirectoryUserSyncEnabled(
            parentOrganisationIdStr: $parentOrganisationIdStr
            childOrganisationIdStr: $childOrganisationIdStr
            enabled: $enabled
        ) {
            idStr
            userSyncEnabled
        }
    }
`

interface ChildDirectory {
    idStr: string
    name: string
    filterRules: string[]
    userSyncEnabled: boolean
}

interface ManageChildDirectoriesProps {
    parentOrganisationIdStr: string
    modal: string
    setModal: (modal: string) => void
}

export const ManageChildDirectories: React.FC<ManageChildDirectoriesProps> = ({
    parentOrganisationIdStr,
    modal,
    setModal,
}) => {
    const alert = useAlert()
    const {refetch: refetchOrganisation} = CurrentOrganisation.useContainer()
    const [name, setName] = useState('')
    const [filterRulesText, setFilterRulesText] = useState('')
    const [showCreate, setShowCreate] = useState(false)

    const {data, loading, error, refetch} = useQuery(CHILD_DIRECTORIES, {
        variables: {parentOrganisationIdStr},
        skip: modal !== 'manageChildDirectories',
    })

    const [createChild, {loading: creating}] = useMutation(CREATE_CHILD, {
        onError: (error) => alert.error(getGraphQLErrorMessage(error)),
    })

    const [deleteChild] = useMutation(DELETE_CHILD, {
        onError: (error) => alert.error(getGraphQLErrorMessage(error)),
    })

    const [detachChild, {loading: detaching}] = useMutation(DETACH_CHILD, {
        onError: (error) => alert.error(getGraphQLErrorMessage(error)),
    })

    const [setUserSyncEnabled, {loading: updatingUserSync}] = useMutation(SET_USER_SYNC_ENABLED, {
        onError: (error) => alert.error(getGraphQLErrorMessage(error)),
    })

    if (modal !== 'manageChildDirectories') return null

    const children: ChildDirectory[] = data?.childDirectoriesForParent || []

    const handleCreate = async () => {
        const filterRules = filterRulesText
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)

        await createChild({
            variables: {
                parentOrganisationIdStr,
                input: {name, filterRules},
            },
        })
        setName('')
        setFilterRulesText('')
        setShowCreate(false)
        await refetch()
        await refetchOrganisation?.()
    }

    const handleDelete = async (childOrganisationIdStr: string) => {
        await deleteChild({
            variables: {parentOrganisationIdStr, childOrganisationIdStr},
        })
        await refetch()
        await refetchOrganisation?.()
    }

    const handleDetach = async (childOrganisationIdStr: string) => {
        await detachChild({
            variables: {parentOrganisationIdStr, childOrganisationIdStr},
        })
        await refetch()
        await refetchOrganisation?.()
    }

    const handleToggleUserSync = async (child: ChildDirectory) => {
        await setUserSyncEnabled({
            variables: {
                parentOrganisationIdStr,
                childOrganisationIdStr: child.idStr,
                enabled: !child.userSyncEnabled,
            },
        })
        await refetch()
    }

    return (
        <ModalLayout>
            <ModalLayout.Navigation closeable={true} narrow={true} onClose={() => setModal('')} />
            <ModalLayout.Body>
                <Typography.Heading>Manage Child Directories</Typography.Heading>
                <Typography.Paragraph style={{marginBottom: 16, color: '#666'}}>
                    Create child directories for preview and profile sync. Enable user sync when ready to release
                    end-user access.
                </Typography.Paragraph>

                {error ? (
                    <Typography.Paragraph>{getGraphQLErrorMessage(error)}</Typography.Paragraph>
                ) : loading ? (
                    <Typography.Paragraph>Loading...</Typography.Paragraph>
                ) : (
                    <>
                        {children.map((child) => (
                            <div
                                key={child.idStr}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8,
                                    padding: 8,
                                    border: '1px solid #eee',
                                }}
                            >
                                <div>
                                    <strong>{child.name}</strong>
                                    <div style={{fontSize: 12, color: child.userSyncEnabled ? '#2a7' : '#a72'}}>
                                        User sync: {child.userSyncEnabled ? 'enabled' : 'disabled (preview)'}
                                    </div>
                                    {child.filterRules?.length > 0 && (
                                        <div style={{fontSize: 12, color: '#666'}}>{child.filterRules.join('; ')}</div>
                                    )}
                                </div>
                                <div style={{display: 'flex', gap: 8}}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            window.location.href = `/${child.idStr}/admin`
                                        }}
                                    >
                                        Open data
                                    </Button>
                                    <Button
                                        variant={child.userSyncEnabled ? 'secondary' : 'primary'}
                                        loading={updatingUserSync}
                                        onClick={() => handleToggleUserSync(child)}
                                    >
                                        {child.userSyncEnabled ? 'Disable user sync' : 'Enable user sync'}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        loading={detaching}
                                        onClick={() => handleDetach(child.idStr)}
                                    >
                                        Detach
                                    </Button>
                                    <Button variant="tertiary" onClick={() => handleDelete(child.idStr)}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        ))}

                        {showCreate ? (
                            <div style={{marginTop: 16}}>
                                <Typography.Paragraph>Directory name</Typography.Paragraph>
                                <VisibleInput
                                    type="text"
                                    value={name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                                />
                                <Typography.Paragraph>Filter expressions (one per line)</Typography.Paragraph>
                                <StyledTextArea
                                    value={filterRulesText}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        setFilterRulesText(e.target.value)
                                    }
                                    rows={6}
                                    style={{width: '100%', fontFamily: 'monospace'}}
                                />
                                <div style={{marginTop: 8, display: 'flex', gap: 8}}>
                                    <Button variant="primary" loading={creating} onClick={handleCreate}>
                                        Create
                                    </Button>
                                    <Button variant="secondary" onClick={() => setShowCreate(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button variant="secondary" onClick={() => setShowCreate(true)} style={{marginTop: 16}}>
                                Add child directory
                            </Button>
                        )}
                    </>
                )}
            </ModalLayout.Body>
        </ModalLayout>
    )
}
